import {
  type I2mDataRow,
  type RawTelemetryData,
  type GpsReading,
  type Device,
  calculateDistance,
} from '@pitbrain/shared'
import useApi from '../../../api/useApi'
import ConfigurationContext from '../../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { useBaseProcessor } from '../useBaseProcessor'
import { type UploadFormat } from '../UploadFormat'

export const useI2mProcessor = (): UploadFormat => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { getFirstCoordinate, processAndCreateSession } = useBaseProcessor()
  const { error: apiError } = api

  const parseI2mToRawTelemetry = (results: I2mDataRow[]): RawTelemetryData => {
    let totalDistance = 0
    const gps: GpsReading[] = results.map((p: I2mDataRow, index, array) => {
      if (index > 0) {
        const prev = array[index - 1]
        totalDistance += calculateDistance(
          prev.latitude,
          prev.longitude,
          p.latitude,
          p.longitude
        )
      }

      return {
        latitude: p.latitude,
        longitude: p.longitude,
        altitude: p.altitude,
        speedMs: p.speed / 3.6,
        timestamp: new Date(p.date),
        distanceMeters: totalDistance,
      }
    })

    return {
      gps,
      gyroscope: undefined,
      accelerometer: undefined,
      recordingStartTime: gps[0]?.timestamp,
      device: {
        deviceName: 'I2M',
        deviceType: 'I2M',
        deviceVersion: '1.0',
      },
    }
  }

  const processFile = async (file: File, sessionId: string): Promise<void> => {
    const results = await api.fileApi.convertDatFile(file)
    const rawTelemetry = parseI2mToRawTelemetry(results)
    const firstCoordinate = getFirstCoordinate(rawTelemetry)
    if (firstCoordinate == null) return

    const track = await api.trackApi.findTrack(firstCoordinate)
    const device: Device = { name: 'I2M', version: '1.0' }

    await processAndCreateSession(
      rawTelemetry,
      sessionId,
      track,
      { hasLeanAngle: false, hasVideo: false },
      file.name,
      device
    )
  }

  return {
    extensions: ['.DAT'],
    keepsFileAsSource: true,
    processFile,
    error: apiError,
  }
}
